import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

const CustomRangeSlider = ({ value, onChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  const [internalValue, setInternalValue] = useState(value ?? 5);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateValue(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      updateValue(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    updateValueTouch(e);
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      updateValueTouch(e);
    }
  };

  const updateValue = (e) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newValue = Math.round(percentage * 10);
    
    setInternalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const updateValueTouch = (e) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newValue = Math.round(percentage * 10);
    
    setInternalValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleMouseUp);
      
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleMouseUp);
      };
    }
  }, [isDragging]);

  const percentage = (internalValue / 10) * 100;
  
  // Color gradient based on value
  const getTrackColor = () => {
    if (value === undefined) return "bg-gray-600";
    if (internalValue < 4) return "bg-gradient-to-r from-red-500 to-orange-500";
    if (internalValue < 7) return "bg-gradient-to-r from-orange-500 to-yellow-500";
    return "bg-gradient-to-r from-yellow-500 to-green-500";
  };

  const getHandleColor = () => {
    if (value === undefined) return "border-gray-500";
    if (internalValue < 4) return "border-red-500";
    if (internalValue < 7) return "border-yellow-500";
    return "border-green-500";
  };

  return (
    <div className="w-full py-4">
      {/* Slider Track */}
      <div 
        ref={sliderRef}
        className="relative h-2 bg-gray-700 rounded-full cursor-pointer"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* Filled Track */}
        {value !== undefined && (
          <div 
            className={`absolute h-full rounded-full ${getTrackColor()} transition-all duration-150`}
            style={{ width: `${percentage}%` }}
          />
        )}
        
        {/* Handle */}
        <motion.div
          className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-lg border-2 ${getHandleColor()} cursor-grab active:cursor-grabbing transition-colors`}
          style={{ left: `${percentage}%`, transform: `translateX(-50%) translateY(-50%)` }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          animate={{
            scale: isDragging ? 0.95 : 1,
          }}
        >
          {/* Value tooltip */}
          {isDragging && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-sm px-2 py-1 rounded-md whitespace-nowrap"
            >
              {internalValue}
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-800" />
            </motion.div>
          )}
        </motion.div>
      </div>
      
      {/* Labels */}
      <div className="flex justify-between mt-3">
        <span className="text-sm font-medium text-gray-400">Disagree</span>
        <span className={`text-sm font-semibold ${value !== undefined ? 'text-gray-300' : 'text-gray-500'}`}>
          {value !== undefined ? internalValue : '-'}
        </span>
        <span className="text-sm font-medium text-gray-400">Agree</span>
      </div>
    </div>
  );
};

export default CustomRangeSlider;